import { create } from 'zustand';

export interface Notification {
  id: string;
  type: 'system' | 'creator' | 'device' | 'automation' | 'ai' | 'scheduling' | 'security';
  title: string;
  description?: string;
  timestamp: string;
  read: boolean;
  severity?: 'info' | 'warning' | 'error' | 'success';
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (n: Notification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearNotifications: () => void;
}

const unread = (notifications: Notification[]): number => notifications.filter((n) => !n.read).length;

// Starts empty. It used to be seeded with three made-up alerts ("SM-G998B went
// offline", …) that every user saw, and its `get unreadCount()` was flattened
// by zustand's first `set`, so the badge never went down. The count is now
// plain state, recomputed on every change.
export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  addNotification: (n) =>
    set((s) => {
      const notifications = [n, ...s.notifications].slice(0, 100);
      return { notifications, unreadCount: unread(notifications) };
    }),
  markRead: (id) =>
    set((s) => {
      const notifications = s.notifications.map((n) => (n.id === id ? { ...n, read: true } : n));
      return { notifications, unreadCount: unread(notifications) };
    }),
  markAllRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),
  clearNotifications: () => set({ notifications: [], unreadCount: 0 }),
}));
